<script setup lang="ts">
import SelectItem from "@/app/components/SelectItem.vue"
import { useCurrencies } from "@/app/composables/useCurrencies"
import { t } from "@/app/composables/useI18n"
import { useTransactions } from "@/app/composables/useTransactions"
import { vClickOutside } from "@/app/directives"
import { cfl, formatLocalDateTime } from "@/app/utils/strings"
import { onMounted } from "vue"

const { getIconByTicker } = useCurrencies()
const {
  loading,
  showMoreButton,
  transactions,
  wallets,
  isWalletOpen,
  isStatusOpen,
  isTypeOpen,
  selectedStatus,
  selectedWallet,
  selectedType,
  statuses,
  types,
  allWalletOption,
  closeAllFilters,
  openWalletFilter,
  openStatusFilter,
  openTypeFilter,
  selectWallet,
  selectStatus,
  selectType,
  loadTransactions,
  loadNextPage,
  loadUserWallets,
} = useTransactions()

function getWalletTitle(wallet: any): string {
  if (wallet.id === null) {
    return t("finance.transactions_all")
  }
  return `${wallet.currency?.ticker}: ${cfl(wallet.type || "")}`
}

onMounted(() => {
  loadUserWallets()
  loadTransactions()
})
</script>

<template>
  <div class="transaction" data-testid="transaction-list">
    <div class="transaction-filters">
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("account.wallet") }}</div>
        <div class="select-filter" @click="openWalletFilter">
          <SelectItem :value="getWalletTitle(selectedWallet)" :is-open="isWalletOpen" name="wallet">
            <template #header>
              <div :key="allWalletOption.id" class="select-filter__item" @click="selectWallet(allWalletOption)">
                {{ getWalletTitle(allWalletOption) }}
              </div>
              <div v-for="wallet in wallets" :key="wallet.id" data-testid="selectFilterItem" class="select-filter__item" @click="selectWallet(wallet)">
                {{ getWalletTitle(wallet) }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("finance.status") }}</div>
        <div class="select-filter" @click="openStatusFilter">
          <SelectItem :value="selectedStatus.title" :is-open="isStatusOpen" name="status">
            <template #header>
              <div v-for="status in statuses" :key="status.title" data-testid="selectFilterItem" class="select-filter__item" @click="selectStatus(status)">
                {{ status.title }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
      <div v-click-outside="closeAllFilters" class="transaction-filter">
        <div>{{ t("finance.type") }}</div>
        <div class="select-filter" @click="openTypeFilter">
          <SelectItem :value="selectedType.title" :is-open="isTypeOpen" name="type">
            <template #header>
              <div v-for="type in types" :key="type.title" data-testid="selectFilterItem" class="select-filter__item" @click="selectType(type)">
                {{ type.title }}
              </div>
            </template>
          </SelectItem>
        </div>
      </div>
    </div>

    <div v-for="transaction in transactions" :key="transaction.id" class="transaction-card" data-testid="transaction-item">
      <div class="card-header" data-testid="cardHeader">
        <div class="header-left">
          <div>&nbsp;{{ cfl(transaction.type) }}</div>
        </div>
        <div :class="`status ${transaction.status}`">
          {{ cfl(transaction.status) }}
        </div>
      </div>
      <div class="card-body" data-testid="cardBody">
        <div class="amount-titles">
          <div>{{ t("finance.amount") }}</div>
          <div v-if="transaction.fee">
            {{ t("finance.fee") }}
          </div>
          <!-- <div v-if="transaction.bonus_name">
            {{ t("finance.bonus") }}
          </div> -->
          <div>{{ t("account.dob_date") }} & {{ t("finance.time") }}</div>
          <div v-if="transaction.desc !== null && transaction.desc !== ''">
            {{ t("finance.description") }}
          </div>
          <div v-if="transaction.message !== null">
            {{ t("finance.message") }}
          </div>
          <div v-if="transaction.crypto_tx_id !== null">
            TX
          </div>
        </div>
        <div class="card--amounts">
          <div>
            <img class="img-icon" :src="getIconByTicker(transaction?.wallet?.currency?.ticker)" alt="">
            {{ transaction.amount }}
          </div>
          <div v-if="transaction.fee">
            {{ transaction.fee }}
          </div>
          <!-- <div v-if="transaction.bonus_name">
            {{ transaction.bonus_name }}
          </div> -->
          <div>{{ formatLocalDateTime(transaction.created_at) }}</div>
          <div v-if="transaction.desc !== null && transaction.desc !== ''">
            {{ transaction.desc }}
          </div>
          <div v-if="transaction.message !== null">
            {{ transaction.message }}
          </div>
          <div v-if="transaction.crypto_tx_id !== null" class="txid">
            {{ transaction.crypto_tx_id }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="!showMoreButton && transactions.length === 0 && loading !== true" class="empty-data" data-testid="emptyData">
      {{ t("general.dont_have") }} {{ t("finance.transactions") }}
    </div>

    <button
      v-if="showMoreButton && transactions.length > 0"
      class="btn-g"
      :disabled="loading"
      @click="loadNextPage()"
    >
      <span v-if="loading">{{ t("finance.loading") }}</span>
      <span v-else>{{ t("finance.more_transactions") }}</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
.img-icon {
  width: 16px;
  height: 16px;
  float: right;
}

.txid {
  width: 260px;
  overflow-y: auto;
}

.btn-g {
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--be-primary-btn);
  margin: 12px 0px;
}

.transaction {
  margin: 16px;
}

.select-item {
  display: flex;
  padding: 10px;
  border-radius: 5px;
  background: var(--be-small-card-bg);
  display: flex;
  justify-content: space-between;
}

.items {
  border-radius: 8px;
  background: #0d0c2c;
  padding: 8px;
  background: var(--be-small-card-bg);
  margin-top: 10px;
}

.transaction-filters {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  .transaction-filter {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
  }

  .select-filter {
    width: 132px;

    &__item {
      cursor: pointer;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media screen and (max-width: 500px) {
    .select-filter {
      width: 113px;
    }
  }
}

.transaction-card {
  margin-top: 1em;
  border-radius: 8px;
  background: var(--be-big-card-bg);
  font-size: 14px;
  padding: 10px 16px;
  // display: flex;
  // justify-content: space-between;

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;

    .header-left {
      display: flex;
    }

    .status {
      border-radius: 4px;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      padding: 6px 8px;
    }

    .success {
      color: #3178d3;
      background: rgba(55, 111, 229, 0.3);
    }
    .initiated {
      background: rgba(72, 72, 72, 0.3);
      color: rgba(255, 255, 255, 0.5);
    }
    .error {
      background: rgba(148, 0, 0, 0.3);
      color: #940000;
    }
    .cancelled {
      background: rgba(147, 52, 1, 0.3);
      color: #943e00;
    }
  }
  .card-body {
    display: flex;
    justify-content: space-between;
  }

  .amount-titles {
    display: flex;
    flex-direction: column;
    gap: 10px 0;

    div {
      white-space: nowrap;
    }
  }
  .card--amounts {
    display: flex;
    word-break: break-word;
    flex-direction: column;
    align-items: end;
    gap: 10px 0;
    margin-left: 10px;

    div {
      text-align: right;
    }
  }
  .card-address {
    display: flex;
    gap: 0 5px;
  }
}
</style>
